import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { EmptyState, ListTable, Panel, StatusChip, type ListColumn } from '../../../ui-kit';
import { fetchPcConfiguration, type PcConfiguration } from '../../../services/policyCheckingService';
import { useLive } from '../rocco/live';
import { SEVERITY } from './status';

type CheckRow = PcConfiguration['checks'][number];

/**
 * How Policy Checking is set up, exactly as it runs. Every check on this page
 * is read from the rules themselves, so the list cannot drift from what the
 * agent actually does. Nothing here is a control.
 */
export default function ConfigurationPage() {
  const { data, error, loading } = useLive(fetchPcConfiguration, 60_000);
  if (error && !data) return <EmptyState message={error} />;

  const columns: ListColumn<CheckRow>[] = [
    {
      key: 'label',
      sortValue: (r) => r.label,
      header: 'Check',
      grow: 1.2,
      minWidth: 170,
      render: (r) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{r.label}</Typography>,
    },
    {
      key: 'area',
      sortValue: (r) => r.area,
      header: 'Part of the policy',
      width: 140,
      render: (r) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{r.area}</Typography>,
    },
    {
      key: 'compares',
      sortValue: (r) => r.compares,
      header: 'Compares',
      grow: 1.4,
      minWidth: 180,
      render: (r) => <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>{r.compares}</Typography>,
    },
    {
      key: 'severity',
      sortValue: (r) => r.severity,
      header: 'If it differs',
      width: 130,
      render: (r) => <StatusChip {...SEVERITY[r.severity]} />,
    },
  ];

  return (
    <Stack spacing={2}>
      <Panel
        title="What it checks"
        subtitle={data ? `${data.checks.length} checks on every policy` : 'Every check on every policy'}
      >
        <ListTable
          columns={columns}
          rows={data?.checks ?? []}
          getRowKey={(r) => `${r.area}-${r.label}`}
          loading={loading}
          emptyMessage="No checks are set up."
        />
      </Panel>

      <Panel title="What the severities mean" subtitle="Which findings need doing, and which need a look"
        loading={loading} loadingHeight={140}>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
          {(data?.severities ?? []).map((one) => (
            <Stack key={one.key} spacing={0.75} alignItems="flex-start">
              <StatusChip {...SEVERITY[one.key]} />
              <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{one.detail}</Typography>
            </Stack>
          ))}
        </Box>
      </Panel>

      <Panel title="How a check runs" subtitle="From the documents landing to the findings"
        loading={loading} loadingHeight={300}>
        <Stack>
          {(data?.rules ?? []).map((rule) => (
            <Box
              key={rule.when}
              sx={(t) => ({
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(200px, 1fr) minmax(200px, 1fr) 2fr' },
                columnGap: 2,
                rowGap: 0.25,
                py: 1.25,
                borderBottom: `1px solid ${t.tokens.border.subtle}`,
                '&:last-of-type': { borderBottom: 'none' },
              })}
            >
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{rule.when}</Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>{rule.then}</Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>{rule.detail}</Typography>
            </Box>
          ))}
        </Stack>
      </Panel>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, alignItems: 'start' }}>
        <Panel title="What it reads from each document" subtitle="The same fixed form every time"
          loading={loading} loadingHeight={220}>
          <Stack spacing={1.25}>
            {(data?.reads ?? []).map((one) => <Line key={one.kind} title={one.label} detail={one.detail} />)}
          </Stack>
        </Panel>

        <Panel title="Guardrails" subtitle="Held on every document and every check"
          loading={loading} loadingHeight={220}>
          <Stack spacing={1.25}>
            {(data?.guardrails ?? []).map((rail) => <Line key={rail.title} title={rail.title} detail={rail.detail} />)}
          </Stack>
        </Panel>
      </Box>
    </Stack>
  );
}

function Line({ title, detail }: { title: string; detail: string }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>{title}</Typography>
      <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{detail}</Typography>
    </Stack>
  );
}
